import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Bandeau, Champ, Liste } from './Champs'
import { jour, libelleBien, libelleStatutBail, mois, montant, nomDeLangue } from './Bail'
import { ErreurApi } from '../api'
import {
  confirmerPaiement,
  contesterPaiement,
  declarerPaiement,
  echeancier as chargerEcheancier,
  mesBaux,
  mesQuittances,
  telechargerQuittance,
  type Bail,
  type Echeance,
  type MoyenPaiement,
  type PeriodeLoyer,
  type Quittance,
  type StatutEcheance,
} from '../apiBail'
import { nombre } from '../formats'
import { interpoler, messageErreur, TEXTES, type Langue } from '../i18n'
import { Bloc, Bouton, EtatVide, Icone, Modale, SqueletteListe, SquelettePage } from '../ui'
import { useVille } from '../VilleContexte'

const MOYENS: MoyenPaiement[] = ['Cash', 'BankTransfer', 'Cheque', 'Other']

type Textes = (typeof TEXTES)['fr']
type Role = 'locataire' | 'bailleur'

/** Une échéance et le mois qu'elle sert : le tableau en fait une ligne. */
interface Ligne {
  periode: PeriodeLoyer
  echeance: Echeance
}

/**
 * « Octobre 2026 » en tête de ligne, de bloc ou de modale. `Intl` rend le mois en minuscules,
 * ce qui est juste dans une phrase et faux en tête de cellule ; l'arabe n'a pas de casse.
 */
function libelleMois(langue: Langue, iso: string): string {
  const texte = mois(langue, iso)

  return texte.charAt(0).toLocaleUpperCase(langue) + texte.slice(1)
}

/**
 * Échéancier — revu le 15/09/2026 sur la maquette `docs/ui/pwa/echeances.html`.
 *
 * <b>Un bail à la fois, et les deux rôles dans le même sélecteur.</b> L'écran listait les
 * échéances ouvertes de tous les baux, sous deux onglets. La maquette choisit un bail — c'est
 * l'unité de lecture d'un loyer : quatre mesures, le mois qui compte, l'historique. Un locataire
 * qui est aussi bailleur ailleurs trouve ses deux baux dans la même liste, chacun avec son rôle.
 *
 * <b>Invariant 9 rendu lisible.</b> Aucun bouton « Payer » n'existe : les deux déclarations —
 * celle qui paie, celle qui reçoit — sont les seuls faits que la plateforme connaît. Le champ
 * « Référence » porte son avertissement au bon endroit : dans le champ, pas dans les CGU.
 */
export function MesEcheances({ langue, moi }: { langue: Langue; moi: string | null }) {
  const t = TEXTES[langue]
  const ville = useVille()
  const [params, setParams] = useSearchParams()
  const [baux, setBaux] = useState<Bail[] | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)

  useEffect(() => {
    /*
      Les deux listes en parallèle, puis une seule. `asLandlord=false` rend les baux dont je suis
      PARTIE — bailleur compris — et `asLandlord=true` ceux dont je suis le bailleur : le rôle ne
      se lit donc pas dans la liste d'origine mais sur le bail lui-même (`landlordUserId`).
      Les baux actifs d'abord : c'est celui qu'on vient lire, les autres sont l'historique.
    */
    void Promise.all([mesBaux(false), mesBaux(true)])
      .then((pages) => {
        const vus = new Map<string, Bail>()

        for (const page of pages) {
          for (const bail of page.items) {
            vus.set(bail.id, bail)
          }
        }

        setBaux(
          [...vus.values()].sort(
            (a, b) => Number(b.status === 'Active') - Number(a.status === 'Active') || b.startDate.localeCompare(a.startDate),
          ),
        )
      })
      .catch((cause: unknown) =>
        setErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal')),
      )
  }, [langue])

  const demande = params.get('bail')
  const choisi = baux?.find((bail) => bail.id === demande) ?? baux?.[0] ?? null
  const roleDe = (bail: Bail): Role => (moi !== null && bail.landlordUserId === moi ? 'bailleur' : 'locataire')

  return (
    <section className="echeancier">
      <div className="page-entete">
        <div className="page-entete__rangee">
          <h2>{t.echeancier}</h2>

          {baux !== null && baux.length > 0 && choisi !== null && (
            <select
              className="saisie echeancier__bail"
              aria-label={t.echeancierBail}
              value={choisi.id}
              onChange={(evenement) => setParams({ bail: evenement.target.value }, { replace: true })}
            >
              {baux.map((bail) => (
                <option key={bail.id} value={bail.id}>
                  {libelleBien(bail, ville.libelle)} —{' '}
                  {roleDe(bail) === 'locataire' ? t.echeancierRoleLocataire : t.echeancierRoleBailleur}
                  {bail.status !== 'Active' && ` · ${libelleStatutBail(t, bail.status)}`}
                </option>
              ))}
            </select>
          )}
        </div>
        <p>{t.echeancierIntro}</p>
      </div>

      {erreur !== null && <Bandeau ton="erreur">{erreur}</Bandeau>}
      {baux === null && erreur === null && <SqueletteListe />}
      {baux !== null && baux.length === 0 && <EtatVide icone="monnaie" titre={t.echeancierAucunBail} />}

      {choisi !== null && (
        <EcheancierBail key={choisi.id} langue={langue} moi={moi} bail={choisi} role={roleDe(choisi)} />
      )}
    </section>
  )
}

function EcheancierBail({ langue, moi, bail, role }: { langue: Langue; moi: string | null; bail: Bail; role: Role }) {
  const t = TEXTES[langue]
  const [periodes, setPeriodes] = useState<PeriodeLoyer[] | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [erreurAction, setErreurAction] = useState<string | null>(null)
  const [aDeclarer, setADeclarer] = useState<Ligne | null>(null)
  const [occupe, setOccupe] = useState(false)

  const charger = useCallback(() => {
    void chargerEcheancier(bail.id)
      .then(setPeriodes)
      .catch((cause: unknown) =>
        setErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal')),
      )
  }, [bail.id, langue])

  useEffect(charger, [charger])

  /*
    Le locataire ne voit que ses quotes-parts ; le bailleur les voit toutes, avec le nom du
    payeur. Du plus récent au plus ancien : ce qu'on cherche est en haut, l'historique dessous.
  */
  const lignes = useMemo<Ligne[]>(() => {
    if (periodes === null) return []

    return periodes
      .flatMap((periode) =>
        periode.installments
          .filter((echeance) => role === 'bailleur' || moi === null || echeance.payerUserId === moi)
          .map((echeance) => ({ periode, echeance })),
      )
      .sort((a, b) => b.echeance.dueDate.localeCompare(a.echeance.dueDate))
  }, [periodes, role, moi])

  const agir = useCallback(
    async (action: () => Promise<unknown>) => {
      setErreurAction(null)
      setOccupe(true)

      try {
        await action()
        setADeclarer(null)
        charger()
      } catch (cause: unknown) {
        setErreurAction(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal'))
      } finally {
        setOccupe(false)
      }
    },
    [charger, langue],
  )

  if (erreur !== null) {
    return <Bandeau ton="erreur">{erreur}</Bandeau>
  }

  if (periodes === null) {
    return <SquelettePage />
  }

  if (lignes.length === 0) {
    return <EtatVide icone="monnaie" titre={t.echeancierVide} />
  }

  // Le mois qui compte : la première échéance ouverte, sinon la prochaine à venir, sinon la
  // dernière — un bail entièrement soldé montre son dernier mois, pas un bloc vide.
  const chronologique = [...lignes].reverse()
  const courante =
    chronologique.find((ligne) => estEnCours(ligne.echeance.status)) ??
    chronologique.find((ligne) => ligne.echeance.status === 'Upcoming') ??
    lignes[0]

  // Ce que le bouton du bas déclare : la première échéance sans déclaration en attente.
  const prochaine =
    chronologique.find(
      (ligne) => estDeclarable(ligne.echeance.status) && ligne.echeance.pendingDeclarationId === null,
    ) ?? null

  const devise = lignes[0].echeance.currencyCode
  const parMois =
    role === 'bailleur'
      ? bail.rentAmount + bail.chargesAmount
      : (lignes.find((ligne) => !ligne.periode.isProrated) ?? lignes[0]).echeance.totalAmount
  const enAttente = lignes.filter((ligne) => ligne.echeance.status === 'DeclaredUnconfirmed')
  const derniereDeclaree = enAttente
    .map((ligne) => ligne.echeance.declaredPaidOn)
    .filter((date): date is string => date !== null)
    .sort()
    .at(-1)

  return (
    <div className="echeancier__corps">
      <div className="mesures">
        <div className="mesure">
          <span className="mesure__valeur">
            <bdi>{nombre(parMois, langue)}</bdi>
          </span>
          <span className="mesure__libelle">{interpoler(t.echeancierParMois, { devise })}</span>
        </div>
        <Mesure
          valeur={lignes.filter((ligne) => ligne.echeance.status === 'Confirmed').length}
          libelle={t.echeancierConfirmees}
          langue={langue}
        />
        <Mesure
          valeur={enAttente.length}
          libelle={t.echeancierEnAttente}
          delta={
            derniereDeclaree === undefined
              ? undefined
              : interpoler(t.echeancierDeclareeLe, { date: jour(langue, derniereDeclaree) })
          }
          langue={langue}
        />
        <Mesure
          valeur={lignes.filter((ligne) => ligne.echeance.status === 'Late').length}
          libelle={t.echeancierEnRetard}
          langue={langue}
        />
      </div>

      <BlocCourant
        t={t}
        langue={langue}
        moi={moi}
        bail={bail}
        role={role}
        ligne={courante}
        occupe={occupe}
        onConfirmer={(id) => void agir(() => confirmerPaiement(id))}
        onContester={(id) => void agir(() => contesterPaiement(id, 'AmountMismatch', null))}
        onDeclarer={() => setADeclarer(courante)}
      />

      {erreurAction !== null && aDeclarer === null && <Bandeau ton="erreur">{erreurAction}</Bandeau>}

      <div className="table-defilante">
        <table>
          <caption className="visuellement-cachee">{t.echeancier}</caption>
          <thead>
            <tr>
              <th scope="col">{t.echeanceMois}</th>
              <th scope="col" className="nombre">
                {t.echeanceMontant}
              </th>
              <th scope="col">{t.echeanceStatut}</th>
              <th scope="col">{t.echeancePayeLe}</th>
              <th scope="col">{t.echeanceMoyenCourt}</th>
              <th scope="col">{t.echeanceAction}</th>
            </tr>
          </thead>
          <tbody>
            {lignes.map((ligne) => (
              <LigneEcheance
                key={ligne.echeance.id}
                t={t}
                langue={langue}
                moi={moi}
                role={role}
                ligne={ligne}
                occupe={occupe}
                onConfirmer={(id) => void agir(() => confirmerPaiement(id))}
                onContester={(id) => void agir(() => contesterPaiement(id, 'AmountMismatch', null))}
                onDeclarer={() => setADeclarer(ligne)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {prochaine !== null && (
        <p className="echeancier__pied">
          <Bouton onClick={() => setADeclarer(prochaine)}>{t.echeancierDeclarerUn}</Bouton>
        </p>
      )}

      {aDeclarer !== null && (
        <ModaleDeclaration
          t={t}
          langue={langue}
          ligne={aDeclarer}
          occupe={occupe}
          erreur={erreurAction}
          onFermer={() => {
            setADeclarer(null)
            setErreurAction(null)
          }}
          onDeclarer={(paidOn, amount, moyen, reference) =>
            void agir(() => declarerPaiement(aDeclarer.echeance.id, paidOn, amount, moyen, reference))
          }
        />
      )}
    </div>
  )
}

function Mesure({
  valeur,
  libelle,
  delta,
  langue,
}: {
  valeur: number
  libelle: string
  delta?: string
  langue: Langue
}) {
  return (
    <div className="mesure">
      <span className="mesure__valeur">{nombre(valeur, langue)}</span>
      <span className="mesure__libelle">{libelle}</span>
      {delta !== undefined && <span className="mesure__delta">{delta}</span>}
    </div>
  )
}

/**
 * Le mois qui compte, en tête : exigible quand, déclaré par qui, et le geste qui reste à faire.
 *
 * <b>Le bouton dépend de qui regarde.</b> Une déclaration en attente affiche « confirmer » à
 * l'autre partie et « en attente » à celle qui l'a déposée : l'invariant « le confirmant n'est
 * jamais le déclarant » se voit donc dans l'interface, au lieu de se découvrir sur un refus.
 */
function BlocCourant({
  t,
  langue,
  moi,
  bail,
  role,
  ligne,
  occupe,
  onConfirmer,
  onContester,
  onDeclarer,
}: {
  t: Textes
  langue: Langue
  moi: string | null
  bail: Bail
  role: Role
  ligne: Ligne
  occupe: boolean
  onConfirmer: (declarationId: string) => void
  onContester: (declarationId: string) => void
  onDeclarer: () => void
}) {
  const { periode, echeance } = ligne
  const enAttente = echeance.pendingDeclarationId !== null
  const jeSuisLeDeclarant = enAttente && echeance.pendingDeclaredByUserId === moi
  const declarant =
    echeance.pendingDeclaredByUserId === echeance.payerUserId
      ? echeance.payerDisplayName
      : bail.landlordDisplayName

  const phrases: string[] = [interpoler(t.echeancierExigibleLe, { date: jour(langue, echeance.dueDate) })]

  if (echeance.declaredPaidOn !== null && echeance.declaredMethod !== null) {
    const valeurs = {
      nom: declarant ?? '—',
      date: jour(langue, echeance.declaredPaidOn),
      moyen: libelleMoyen(t, echeance.declaredMethod).toLocaleLowerCase(langue),
    }

    if (echeance.status === 'DeclaredUnconfirmed') {
      phrases.push(interpoler(jeSuisLeDeclarant ? t.echeancierVousAvezDeclare : t.echeancierADeclare, valeurs))
    } else if (echeance.status === 'Disputed') {
      phrases.push(interpoler(t.echeancierConteste, valeurs))
    } else if (echeance.status === 'Confirmed') {
      phrases.push(interpoler(t.echeancierConfirme, valeurs))
    }
  } else if (echeance.status === 'Due' || echeance.status === 'Late') {
    phrases.push(t.echeancierAucuneDeclaration)
  }

  return (
    <div className="bloc bloc--accent echeancier__courante">
      <div className="rangee rangee--espace rangee--haut">
        <div>
          <h3>
            {libelleMois(langue, periode.periodStart)} — <bdi>{montant(langue, echeance.totalAmount, echeance.currencyCode)}</bdi>
            {role === 'bailleur' && echeance.payerDisplayName !== null && (
              <span className="echeancier__payeur"> · {echeance.payerDisplayName}</span>
            )}
          </h3>
          <p className="secondaire">{phrases.join(' ')}</p>
        </div>

        <div className="rangee rangee--serree">
          <span className={`statut-point ${pointStatut(echeance.status)}`}>
            {enAttente && jeSuisLeDeclarant ? t.echeanceEnAttenteAutre : libelleStatut(t, echeance.status)}
          </span>

          {enAttente && !jeSuisLeDeclarant && (
            <>
              <Bouton taille="sm" charge={occupe} onClick={() => onConfirmer(echeance.pendingDeclarationId!)}>
                {t.echeanceConfirmer}
              </Bouton>
              <Bouton
                apparence="discret"
                taille="sm"
                desactive={occupe}
                onClick={() => onContester(echeance.pendingDeclarationId!)}
              >
                {t.echeanceContester}
              </Bouton>
            </>
          )}

          {!enAttente && estDeclarable(echeance.status) && (
            <Bouton apparence="secondaire" taille="sm" desactive={occupe} onClick={onDeclarer}>
              {t.echeancierDeclarerCourt}
            </Bouton>
          )}
        </div>
      </div>
    </div>
  )
}

/** Une ligne du tableau dense : le mois, le montant, l'état, ce qui a été déclaré, et l'action. */
function LigneEcheance({
  t,
  langue,
  moi,
  role,
  ligne,
  occupe,
  onConfirmer,
  onContester,
  onDeclarer,
}: {
  t: Textes
  langue: Langue
  moi: string | null
  role: Role
  ligne: Ligne
  occupe: boolean
  onConfirmer: (declarationId: string) => void
  onContester: (declarationId: string) => void
  onDeclarer: () => void
}) {
  const { periode, echeance } = ligne
  const enAttente = echeance.pendingDeclarationId !== null
  const jeSuisLeDeclarant = enAttente && echeance.pendingDeclaredByUserId === moi

  return (
    <tr>
      <td className="echeancier__mois">
        {libelleMois(langue, periode.periodStart)}
        {periode.isProrated && <span className="tertiaire"> {t.echeanceProrata}</span>}
        {role === 'bailleur' && echeance.payerDisplayName !== null && (
          <span className="echeancier__payeur">{echeance.payerDisplayName}</span>
        )}
      </td>
      <td className="nombre">
        <bdi>{montant(langue, echeance.totalAmount, echeance.currencyCode)}</bdi>
      </td>
      <td>
        <span className={`statut-point ${pointStatut(echeance.status)}`}>{libelleStatut(t, echeance.status)}</span>
      </td>
      <td className={echeance.declaredPaidOn === null ? 'secondaire' : undefined}>
        {echeance.declaredPaidOn === null ? '—' : jour(langue, echeance.declaredPaidOn)}
      </td>
      <td className={echeance.declaredMethod === null ? 'secondaire' : undefined}>
        {echeance.declaredMethod === null ? '—' : libelleMoyen(t, echeance.declaredMethod)}
      </td>
      <td>
        {enAttente && jeSuisLeDeclarant && <span className="secondaire">{t.echeancierEnAttenteCourt}</span>}

        {enAttente && !jeSuisLeDeclarant && (
          <span className="rangee rangee--serree">
            <Bouton
              apparence="discret"
              taille="sm"
              desactive={occupe}
              onClick={() => onConfirmer(echeance.pendingDeclarationId!)}
            >
              {t.echeanceConfirmer}
            </Bouton>
            <Bouton
              apparence="discret"
              taille="sm"
              desactive={occupe}
              onClick={() => onContester(echeance.pendingDeclarationId!)}
            >
              {t.echeanceContester}
            </Bouton>
          </span>
        )}

        {!enAttente && estEnCours(echeance.status) && (
          <Bouton apparence="discret" taille="sm" desactive={occupe} onClick={onDeclarer}>
            {t.echeancierDeclarerCourt}
          </Bouton>
        )}

        {echeance.status === 'Confirmed' && echeance.receiptId !== null && (
          <Link to="/quittances">{t.echeancierQuittance}</Link>
        )}

        {/* Un mois à venir se déclare par le bouton du bas, pas depuis douze lignes identiques. */}
        {!enAttente && !estEnCours(echeance.status) && !(echeance.status === 'Confirmed' && echeance.receiptId !== null) && (
          <span className="secondaire">—</span>
        )}
      </td>
    </tr>
  )
}

/**
 * La feuille de déclaration.
 *
 * Le montant est pré-rempli et modifiable : une quote-part réglée en deux fois se déclare en deux
 * fois, et c'est le serveur qui la marque « partiellement payée ». La quittance n'est pas promise
 * ici — elle est émise à la confirmation, et le bandeau le dit avant le bouton.
 */
function ModaleDeclaration({
  t,
  langue,
  ligne,
  occupe,
  erreur,
  onFermer,
  onDeclarer,
}: {
  t: Textes
  langue: Langue
  ligne: Ligne
  occupe: boolean
  erreur: string | null
  onFermer: () => void
  onDeclarer: (paidOn: string, amount: number, moyen: MoyenPaiement, reference: string | null) => void
}) {
  const { periode, echeance } = ligne
  const [montantSaisi, setMontantSaisi] = useState(String(echeance.totalAmount))
  const [paidOn, setPaidOn] = useState(() => new Date().toISOString().slice(0, 10))
  const [moyen, setMoyen] = useState<MoyenPaiement>('Cash')
  const [reference, setReference] = useState('')

  const valeur = Number(montantSaisi)
  const valide = paidOn !== '' && Number.isFinite(valeur) && valeur > 0

  const soumettre = () =>
    onDeclarer(paidOn, valeur, moyen, reference.trim() === '' ? null : reference.trim())

  return (
    <Modale
      feuille
      titre={interpoler(t.echeancierDeclarerTitre, { mois: libelleMois(langue, periode.periodStart) })}
      onFermer={onFermer}
      pied={
        <>
          <Bouton apparence="discret" onClick={onFermer}>
            {t.annuler}
          </Bouton>
          <Bouton charge={occupe} desactive={!valide} onClick={soumettre}>
            {t.echeanceDeclarer}
          </Bouton>
        </>
      }
    >
      <form
        className="echeancier__declaration"
        onSubmit={(evenement) => {
          evenement.preventDefault()
          if (valide) soumettre()
        }}
      >
        {erreur !== null && <Bandeau ton="erreur">{erreur}</Bandeau>}

        <Champ
          id="declaration-montant"
          libelle={t.echeanceMontant}
          type="number"
          valeur={montantSaisi}
          suffixe={echeance.currencyCode}
          onChange={setMontantSaisi}
        />
        <Champ id="declaration-date" libelle={t.echeancePayeLe} type="date" valeur={paidOn} onChange={setPaidOn} />
        <Liste
          id="declaration-moyen"
          libelle={t.echeanceMoyen}
          valeur={moyen}
          options={MOYENS.map((code) => ({ valeur: code, libelle: libelleMoyen(t, code) }))}
          onChange={(code) => setMoyen(code as MoyenPaiement)}
        />
        {/*
          D14 : une photo de virement porte un RIB, et l'invariant 8 l'interdit. L'interface le
          dit plutôt que de laisser quelqu'un chercher où téléverser une preuve.
        */}
        <Champ
          id="declaration-reference"
          libelle={t.echeanceReference}
          valeur={reference}
          aide={t.echeanceReferenceAide}
          onChange={(texte) => setReference(texte.slice(0, 120))}
        />

        <Bandeau ton="info" icone={<Icone nom="info" />}>
          {t.echeancierDeclarerAide}
        </Bandeau>
      </form>
    </Modale>
  )
}

/**
 * Mes quittances — revu le 15/09/2026 sur la maquette `docs/ui/pwa/quittances.html`.
 *
 * <b>Un tableau dense, et l'aperçu du document en dessous.</b> Le tableau dit ce qui existe ;
 * l'aperçu montre ce que le PDF porte — les mêmes faits, figés à l'émission — sans l'ouvrir. Le
 * numéro d'une ligne choisit la quittance à prévisualiser ; par défaut, la plus récente non annulée.
 *
 * <b><code>isRendered</code> se lit avant de proposer un téléchargement</b> : une quittance est
 * numérotée à la confirmation et son PDF rendu juste après (D12). Promettre le fichier pendant ces
 * quelques secondes produirait une erreur là où il n'y a qu'une attente.
 */
export function Quittances({ langue }: { langue: Langue }) {
  const t = TEXTES[langue]
  const [liste, setListe] = useState<Quittance[] | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [apercu, setApercu] = useState<string | null>(null)
  /** La quittance en cours de téléchargement : un `fetch` authentifié, pas un lien (voir `telecharger`). */
  const [enCours, setEnCours] = useState<string | null>(null)

  const telechargerLa = async (quittance: Quittance) => {
    setErreur(null)
    setEnCours(quittance.id)

    try {
      await telechargerQuittance(quittance.id, quittance.number)
    } catch (cause: unknown) {
      setErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal'))
    } finally {
      setEnCours(null)
    }
  }

  useEffect(() => {
    void mesQuittances()
      .then((page) => setListe(page.items))
      .catch((cause: unknown) =>
        setErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal')),
      )
  }, [langue])

  const choisie =
    liste?.find((quittance) => quittance.id === apercu) ??
    liste?.find((quittance) => quittance.cancelledAt === null) ??
    liste?.[0] ??
    null

  return (
    <section className="quittances">
      <div className="page-entete">
        <h2>{t.quittances}</h2>
        <p>{t.quittancesIntro}</p>
      </div>

      {erreur !== null && <Bandeau ton="erreur">{erreur}</Bandeau>}
      {liste === null && erreur === null && <SqueletteListe />}
      {liste !== null && liste.length === 0 && <EtatVide icone="fichier" titre={t.quittancesVides} />}

      {liste !== null && liste.length > 0 && (
        <div className="table-defilante">
          <table>
            <caption className="visuellement-cachee">{t.quittances}</caption>
            <thead>
              <tr>
                <th scope="col">{t.quittanceNumero}</th>
                <th scope="col">{t.quittancePeriode}</th>
                <th scope="col" className="nombre">
                  {t.echeanceMontant}
                </th>
                <th scope="col">{t.quittanceEmiseLe}</th>
                <th scope="col">{t.quittanceLangue}</th>
                <th scope="col">{t.quittanceEtat}</th>
                <th scope="col">{t.echeanceAction}</th>
              </tr>
            </thead>
            <tbody>
              {liste.map((quittance) => {
                const disponible = quittance.cancelledAt === null && quittance.isRendered

                return (
                  <tr
                    key={quittance.id}
                    className={quittance.cancelledAt !== null ? 'est-estompee' : undefined}
                    aria-current={choisie?.id === quittance.id ? 'true' : undefined}
                  >
                    <td className="nombre">
                      <button type="button" className="lien" onClick={() => setApercu(quittance.id)}>
                        {quittance.number}
                      </button>
                    </td>
                    <td>{libelleMois(langue, quittance.periodStart)}</td>
                    <td className="nombre">
                      <bdi>{montant(langue, quittance.totalAmount, quittance.currencyCode)}</bdi>
                    </td>
                    <td>{jour(langue, quittance.issuedAt)}</td>
                    <td>{nomDeLangue(quittance.language)}</td>
                    <td>
                      <span className={`statut-point ${pointQuittance(quittance)}`}>{etatQuittance(t, quittance)}</span>
                    </td>
                    <td>
                      {disponible ? (
                        <button
                          type="button"
                          className="lien"
                          disabled={enCours === quittance.id}
                          onClick={() => void telechargerLa(quittance)}
                        >
                          <Icone nom="telechargement" taille="sm" /> {t.quittanceTelecharger}
                        </button>
                      ) : (
                        <span className="secondaire">—</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {choisie !== null && (
        <ApercuQuittance
          t={t}
          langue={langue}
          quittance={choisie}
          enCours={enCours === choisie.id}
          onTelecharger={() => void telechargerLa(choisie)}
        />
      )}
    </section>
  )
}

/**
 * L'aperçu d'une quittance : ce que le PDF dit, dans la langue de l'écran.
 *
 * Le document lui-même a été engendré dans UNE langue — celle de la colonne « Langue » — et n'en
 * change pas ; l'aperçu, lui, suit l'interface. L'empreinte n'apparaît que si le PDF est rendu :
 * elle porte sur ses octets.
 */
function ApercuQuittance({
  t,
  langue,
  quittance,
  enCours,
  onTelecharger,
}: {
  t: (typeof TEXTES)['fr']
  langue: Langue
  quittance: Quittance
  enCours: boolean
  onTelecharger: () => void
}) {
  const disponible = quittance.cancelledAt === null && quittance.isRendered

  return (
    <Bloc
      titre={<h3>{interpoler(t.quittanceApercuDe, { numero: quittance.number })}</h3>}
      actions={
        disponible ? (
          <Bouton apparence="secondaire" taille="sm" charge={enCours} onClick={onTelecharger}>
            <Icone nom="telechargement" taille="sm" /> {t.quittanceTelecharger}
          </Bouton>
        ) : undefined
      }
    >
      <div className="quittance-apercu">
        {quittance.cancelledAt !== null && (
          <Bandeau ton="erreur">{interpoler(t.quittanceAnnuleeLe, { date: jour(langue, quittance.cancelledAt) })}</Bandeau>
        )}

        <div className="rangee rangee--espace rangee--haut">
          <div>
            <p className="fort quittance-apercu__titre">{t.quittanceApercuTitre}</p>
            <p className="secondaire nombre">N° {quittance.number}</p>
          </div>
          <div className="tertiaire quittance-apercu__meta">
            <p>
              {t.quittanceEmiseLe} {jour(langue, quittance.issuedAt)}
            </p>
            <p>{nomDeLangue(quittance.language)}</p>
          </div>
        </div>

        <dl className="fiche__lignes">
          <div className="fiche__ligne">
            <dt>{t.quittanceBailleur}</dt>
            <dd>{quittance.landlordDisplayName}</dd>
          </div>
          <div className="fiche__ligne">
            <dt>{t.quittanceLocataire}</dt>
            <dd>{quittance.tenantDisplayName}</dd>
          </div>
          <div className="fiche__ligne">
            <dt>{t.quittanceLogement}</dt>
            <dd>{quittance.propertyLabel}</dd>
          </div>
          <div className="fiche__ligne">
            <dt>{t.quittancePeriode}</dt>
            <dd>
              {jour(langue, quittance.periodStart)} – {jour(langue, quittance.periodEnd)}
            </dd>
          </div>
        </dl>

        <div className="bloc bloc--sourd">
          <dl className="fiche__lignes quittance-apercu__montants">
            <div className="fiche__ligne">
              <dt>{t.quittanceLoyerHorsCharges}</dt>
              <dd>
                <bdi>{montant(langue, quittance.rentAmount, quittance.currencyCode)}</bdi>
              </dd>
            </div>
            <div className="fiche__ligne">
              <dt>{t.quittanceCharges}</dt>
              <dd>
                <bdi>{montant(langue, quittance.chargesAmount, quittance.currencyCode)}</bdi>
              </dd>
            </div>
            <div className="fiche__ligne quittance-apercu__total">
              <dt className="fort">{t.quittanceTotal}</dt>
              <dd className="fort">
                <bdi>{montant(langue, quittance.totalAmount, quittance.currencyCode)}</bdi>
              </dd>
            </div>
          </dl>
        </div>

        <p className="secondaire">
          {interpoler(t.quittancePaiementRecu, {
            date: jour(langue, quittance.paidOn),
            moyen: libelleMoyen(t, quittance.method).toLocaleLowerCase(langue),
          })}
        </p>

        {quittance.contentHash !== null && (
          <p className="tertiaire">
            {interpoler(t.quittanceEmpreinte, {
              empreinte: `${quittance.contentHash.slice(0, 8)}…${quittance.contentHash.slice(-4)}`,
            })}
          </p>
        )}
      </div>
    </Bloc>
  )
}

function etatQuittance(t: (typeof TEXTES)['fr'], quittance: Quittance): string {
  if (quittance.cancelledAt !== null) return t.quittanceAnnulee
  if (!quittance.isRendered) return t.quittanceEnCours

  return t.quittanceDisponible
}

function pointQuittance(quittance: Quittance): string {
  if (quittance.cancelledAt !== null) return 'statut-point--danger'
  if (!quittance.isRendered) return 'statut-point--attente'

  return 'statut-point--succes'
}

/** Une échéance qui attend quelque chose de quelqu'un — c'est elle qui passe en tête. */
function estEnCours(statut: StatutEcheance): boolean {
  return (
    statut === 'Due' ||
    statut === 'Late' ||
    statut === 'DeclaredUnconfirmed' ||
    statut === 'Disputed' ||
    statut === 'PartiallyPaid'
  )
}

/** Une échéance sur laquelle une déclaration a un sens — y compris un mois à venir, payé d'avance. */
function estDeclarable(statut: StatutEcheance): boolean {
  return statut === 'Due' || statut === 'Late' || statut === 'Upcoming' || statut === 'PartiallyPaid'
}

function pointStatut(statut: StatutEcheance): string {
  switch (statut) {
    case 'Confirmed':
      return 'statut-point--succes'
    case 'DeclaredUnconfirmed':
    case 'PartiallyPaid':
      return 'statut-point--attente'
    case 'Disputed':
    case 'Late':
      return 'statut-point--danger'
    default:
      return ''
  }
}

function libelleStatut(t: Textes, statut: StatutEcheance): string {
  switch (statut) {
    case 'Upcoming':
      return t.statutUpcoming
    case 'Due':
      return t.statutDue
    case 'DeclaredUnconfirmed':
      return t.statutDeclaredUnconfirmed
    case 'Confirmed':
      return t.statutConfirmed
    case 'Disputed':
      return t.statutDisputed
    case 'Late':
      return t.statutLate
    case 'PartiallyPaid':
      return t.statutPartiallyPaid
    case 'Waived':
      return t.statutWaived
    case 'Cancelled':
      return t.statutCancelled
    default:
      return statut
  }
}

function libelleMoyen(t: Textes, moyen: MoyenPaiement): string {
  switch (moyen) {
    case 'Cash':
      return t.moyenCash
    case 'BankTransfer':
      return t.moyenBankTransfer
    case 'Cheque':
      return t.moyenCheque
    default:
      return t.moyenOther
  }
}
